import { FC, useMemo } from 'react';

import { Box, Divider, styled, Typography } from '@mui/material';

import moment from 'moment/moment';

import { useFormContext, useWatch } from 'react-hook-form';

import { OptionProps } from 'react-select';

import { useStores } from 'mobx/hooks/useStores';

import { getFormattedPhoneNumber } from 'utils/PhoneUtils';

import { FEATURES } from 'constants/features';

import PatientProvider from 'models/PatientProvider';

import { useFeature } from 'hooks/useFeature';

import { DrugSpecificProtocolForm } from 'views/Modals/ScheduledProtocolModal';
import { DrugSpecificProtocolActivationFields } from 'views/Widgets/DrugSpecificProtocolActivationFields';
import { InfoText } from 'views/Widgets/InfoText';

import { usePatientContext } from 'components/Patient/PatientContextProvider';
import { FormMultiAutocomplete, FormSelect } from 'components/UIkit/atoms/Dropdown';

import { MenuOptionSubtitle } from 'components/UIkit/atoms/Dropdown/Select/ReactSelectOptionComponent';
import { StyledMenuOption } from 'components/UIkit/atoms/Dropdown/Select/ReactSelectOptionComponent';
import { ISelectOption } from 'components/UIkit/atoms/Dropdown/Select.shared';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  patientId?: string;
}

export const CustomDrugSpecificFields: FC<Props> = () => {
  const { control } = useFormContext<DrugSpecificProtocolForm>();
  const { providersStore } = useStores();
  const isTxoDirectEnabled = useFeature(FEATURES.SEND_AFTER_HOURS_ALERTS_TO_TXO_DIRECT);

  const patient = usePatientContext();
  const providerOptions = useMemo(() => {
    const sortProviders = (providers: ISelectOption<PatientProvider>[]) => {
      return providers.sort(
        (a: ISelectOption<PatientProvider>, b: ISelectOption<PatientProvider>) => {
          if (a.value?.id === patient?.providerId) {
            return -1;
          }
          if (b.value?.id === patient?.providerId) {
            return 1;
          }
          return a.label.localeCompare(b.label);
        }
      );
    };

    const mappedProviders = providersStore.providersForSelect.map((option) => {
      const email = option.value?.email ?? '';
      const phone = getFormattedPhoneNumber(option.value?.phoneNumber ?? '');

      const isDisabled = isTxoDirectEnabled
        ? // TXO only: disable if phone or email are missing
          !phone || !email
        : // otherwise: disable if phone is missing (ignore email)
          !phone;

      return {
        ...option,
        isDisabled,
        subtitle: getProviderSubtitle(isTxoDirectEnabled, phone, email)
      };
    });

    const enabledProviders = mappedProviders.filter((provider) => !provider.isDisabled);
    const disabledProviders = mappedProviders.filter((provider) => provider.isDisabled);

    return [
      {
        value: null,
        label: 'Nobody',
        dividerAfter: providersStore.providersForSelect.length > 0
      },
      ...sortProviders(enabledProviders),
      ...sortProviders(disabledProviders)
    ];
  }, [providersStore.providersForSelect, patient?.providerId, isTxoDirectEnabled]);

  const [reportTimes] = useWatch({
    control,
    name: ['reportTimes']
  });

  const formattedReportTimes = reportTimes.map((time) => moment(time, 'HH:mm').format('h:mm A'));

  const notificationText = getNotificationText(isTxoDirectEnabled);

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%">
        <Box display="flex" flexShrink={0} alignItems="center" gap="1.2rem" pt={20} pb={40}>
          <Text variant="form-text" flexShrink={0}>
            Report at
          </Text>
          <StyledCustomTimesSelect>
            <FormMultiAutocomplete
              options={[]}
              placeholder={formattedReportTimes.join(', ')}
              name="report-times"
              isDisabled
            />
          </StyledCustomTimesSelect>
        </Box>
      </Box>

      <DrugSpecificProtocolActivationFields />

      <Box display="flex" pt={20}>
        <InfoText text="NOTIFICATIONS" textVarient="body2">
          {notificationText}
        </InfoText>
      </Box>

      <Box display="flex" width="100%" alignItems="center" flexGrow={1} gap={20}>
        <Text variant="form-text">Send urgent after-hours report notifications to</Text>
        <Box flexGrow={1}>
          <FormSelect
            sortAlphabetically={false}
            options={providerOptions}
            isRequired
            isSearchable
            name="afterHoursProvider"
            placeholder="Provider"
            getOptionLabel={(option: ISelectOption<PatientProvider>) =>
              option.value?.id === patient?.providerId ? `Primary (${option.label})` : option.label
            }
            components={{ Option: CustomProviderOption }}
          />
        </Box>
      </Box>
    </>
  );
};

const getNotificationText = (isTxoDirectEnabled: boolean) =>
  isTxoDirectEnabled
    ? 'After-Hours Provider Notification sends an SMS and TXO Direct notification whenever an urgent or relevant report is submitted outside business hours (weekdays 5:00 PM–8:00 AM or weekends), or when a scheduled report is missed during those periods.'
    : 'After-Hours Provider Notification sends an SMS text message to the provider if a relevant / urgent report is submitted weekdays 5:00 PM - 8:00 AM, or weekends.';

const getProviderSubtitle = (
  isTxoDirectEnabled: boolean,
  phone: string,
  email: string
): string | [string, string] => {
  if (isTxoDirectEnabled) {
    if (phone && email) return [`SMS: ${phone}`, `TXO Direct: ${email}`];
    else if (!phone && !email) return 'No Phone & TXO Direct Email – Contact Administrator';
    else if (!phone && email) return 'No Phone – Contact Administrator';
    else if (phone && !email) return 'No TXO Direct Email – Contact Administrator';
  }
  if (phone) return `SMS: ${phone}`;
  return 'No Phone - Contact Administrator';
};

const CustomProviderOption: FC<
  OptionProps<ISelectOption<PatientProvider> & { dividerAfter: boolean }>
> = (props) => (
  <>
    <StyledMenuOption {...props}>
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Typography variant="form-text">{props.children}</Typography>
        <MenuOptionSubtitle subtitle={props.data.subtitle} />
      </Box>
    </StyledMenuOption>

    {props.data.dividerAfter && <Divider />}
  </>
);

const StyledCustomTimesSelect = styled(Box)`
  width: 100%;
`;
